/**
 * Scrubbing de PHI/PII para payloads Sentry (mensagens, extra, contexts).
 * Dados de teste apenas nos testes — nunca PHI real.
 */

const EMAIL_LIKE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi
const UUID_LIKE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi

/** CPF com ou sem máscara (validação de formato, não dígitos verificadores). */
export const CPF_LIKE =
  /\b(?:\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{11})\b/g

/** Telefone BR: +55, DDD, celular/fixo comuns. */
export const BR_PHONE_LIKE =
  /(?:\+55\s*)?(?:\(?\d{2}\)?\s*)?(?:9\s*)?\d{4}[-\s]?\d{4}\b/g

const SENSITIVE_KEY =
  /(patient|paciente|name|nome|cpf|cnpj|phone|telefone|email|transcript|anamnes|complaint|queixa|conduct|conduta|summary|resumo|diagnosis|diagn[oó]stico|prescription|receita|document|prontu[aá]rio|clinical|cl[ií]nico|address|endere[cç]o|birth|nascimento|mother|m[aã]e)/i

const ALLOWED_SCALAR_KEYS = new Set([
  'id',
  'user_id',
  'doctor_id',
  'consultation_id',
  'chunk_id',
  'session_id',
  'edge_function',
  'layer',
  'postgrestcode',
  'code',
  'status',
  'error_code',
  'provider',
  'model',
  'key_source',
  'request_origin',
  'is_fallback',
  'level',
  'category',
])

export const redactPhiInPlainText = (value: string, maxLen = 2000): string =>
  value
    .replace(EMAIL_LIKE, '[redacted-email]')
    .replace(UUID_LIKE, '[redacted-id]')
    .replace(CPF_LIKE, '[redacted-cpf]')
    .replace(BR_PHONE_LIKE, '[redacted-phone]')
    .slice(0, maxLen)

const scrubUnknownValue = (value: unknown, depth: number): unknown => {
  if (depth > 6) return '[redacted-depth]'
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return redactPhiInPlainText(value, 500)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => scrubUnknownValue(item, depth + 1))
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      const lower = key.toLowerCase()
      if (SENSITIVE_KEY.test(key)) {
        out[key] = '[redacted]'
        continue
      }
      if (ALLOWED_SCALAR_KEYS.has(lower)) {
        if (typeof raw === 'string') out[key] = redactPhiInPlainText(raw, 120)
        else if (typeof raw === 'number' || typeof raw === 'boolean') out[key] = raw
        else out[key] = '[redacted]'
        continue
      }
      out[key] = scrubUnknownValue(raw, depth + 1)
    }
    return out
  }
  return '[redacted]'
}

export const scrubSentryExtra = (extra: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!extra || typeof extra !== 'object') return extra
  return scrubUnknownValue(extra, 0) as Record<string, unknown>
}

export const scrubSentryContexts = (
  contexts: Record<string, Record<string, unknown>> | undefined,
): Record<string, Record<string, unknown>> | undefined => {
  if (!contexts || typeof contexts !== 'object') return contexts
  const next: Record<string, Record<string, unknown>> = {}
  for (const [name, ctx] of Object.entries(contexts)) {
    if (!ctx || typeof ctx !== 'object') continue
    if (name === 'synap') {
      next[name] = ctx
      continue
    }
    next[name] = scrubUnknownValue(ctx, 0) as Record<string, unknown>
  }
  return next
}
